// src/modules/app-views/crm/pages/Document/tabs/RecordTab/OpportunityCollapse.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect } from 'react';
import { Col, Row, Collapse } from 'antd';
import { FormOutlined, UnorderedListOutlined, CodeOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import axiosService from 'services/axiosService';
import { API, apiUrl } from 'constants/Api';
import translateX from 'utils/translateX';
import LeadAdditionalInfoContent from 'modules/app-views/leads/pages/Document/tabs/RecordTab/LeadAdditionalInfoCard';
import LeadPayloadContent from 'modules/app-views/leads/pages/Document/tabs/RecordTab/LeadPayloadCard';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OpportunityCollapse = ({ activeKey, onCollapseChange }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const leadId = data?.leadId;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [leadData, setLeadData] = useState(null);
  // Lead ghost collapses start CLOSED (empty array = all closed)
  const [leadCollapseKeys, setLeadCollapseKeys] = useState([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch the origin lead document when this opportunity was created from a lead
  useEffect(() => {
    if (!leadId) {
      setLeadData(null);
      return;
    }

    let cancelled = false;

    fetchLead(leadId).then(result => {
      if (!cancelled) setLeadData(result);
    });

    return () => { cancelled = true; };
  }, [leadId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Collapse
      size="small"
      activeKey={activeKey}
      onChange={onCollapseChange}
      style={{ marginBottom: 12 }}
      styles={{
        header: { alignItems: 'center' },
        body: { padding: 20 },
      }}
      items={[
        {
          key: 'opportunity',
          forceRender: true,
          label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', height: 40 }}>
              <FormOutlined style={{ marginRight: 6 }} />
              {translateX('opportunity')}
            </span>
          ),
          children: (
            <>
              <Row gutter={16}>
                <Col xs={24}>
                  <SimpleTextArea
                    name="note"
                    rows={3}
                    label="opportunity_description"
                  />
                </Col>
              </Row>

              {/* Ghost collapses for origin lead data (always closed by default) */}
              {leadData && (
                <Collapse
                  ghost
                  size="small"
                  activeKey={leadCollapseKeys}
                  onChange={setLeadCollapseKeys}
                  style={{ marginTop: 12 }}
                  items={[
                    {
                      key: 'leadAdditionalInfo',
                      forceRender: true,
                      label: (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <UnorderedListOutlined style={{ marginRight: 6 }} />
                          {translateX('lead_additional_info')}
                        </span>
                      ),
                      children: <LeadAdditionalInfoContent lead={leadData} />,
                    },
                    {
                      key: 'leadPayload',
                      forceRender: true,
                      label: (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <CodeOutlined style={{ marginRight: 6 }} />
                          {translateX('lead_payload')}
                        </span>
                      ),
                      children: <LeadPayloadContent lead={leadData} />,
                    },
                  ]}
                />
              )}
            </>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OpportunityCollapse;

// *******************************************************************************
// HELPERS
// *******************************************************************************
async function fetchLead(leadId) {
  try {
    const url = apiUrl.build({ url: API.LEADS.URL, params: { _id: leadId } });
    const [result] = await axiosService.get({ url, returnAsObject: true });
    return result || null;
  } catch {
    return null;
  }
}
